import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { notify } from "@/shared/feedback/notify";

const menuItemClass =
  "block w-full rounded-[var(--radius-md)] px-2.5 py-1.5 text-left text-sm text-[var(--color-foreground)] no-underline transition-colors hover:bg-[var(--color-background)]";

/**
 * Single-row ⋯ menu on desk doc lists — plan/35 D.
 */
export function DeskDocListRowMenu(props: {
  docPath: string;
  /** Jump targets e.g. Collect / Deliver / Pay / Receive — plan/35 D. */
  tabLinks: { label: string; to: string }[];
  /** When set, first menu item opens the doc assignees panel. */
  assignPath?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function copyLink() {
    const path = props.docPath.startsWith("/")
      ? `${window.location.origin}${props.docPath}`
      : props.docPath;
    try {
      await navigator.clipboard.writeText(path);
      notify.success("Link copied");
    } catch {
      notify.error("Could not copy link");
    }
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative flex justify-end">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 px-0 text-base leading-none text-[var(--color-muted)]"
        aria-label="Row actions"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        ⋯
      </Button>
      {open ? (
        <div
          className={cn(
            "absolute right-0 top-full z-30 mt-1 min-w-[11rem] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-[var(--shadow-soft)]",
          )}
          role="menu"
          aria-label="Row actions"
        >
          {props.assignPath ? (
            <Link
              to={props.assignPath}
              role="menuitem"
              className={menuItemClass}
              onClick={() => setOpen(false)}
            >
              Assign
            </Link>
          ) : null}
          {props.tabLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              className={menuItemClass}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <button
            type="button"
            role="menuitem"
            className={menuItemClass}
            onClick={() => void copyLink()}
          >
            Copy link
          </button>
        </div>
      ) : null}
    </div>
  );
}
